import { create } from 'zustand'
import type { Property, PropertyType } from '../types/api.types'
import { propertiesService } from '../services/propertiesService'

interface PropertyStore {
  properties: Property[]
  selectedPropertyId: number | null
  loading: boolean
  // False until a fetch has been attempted. Until then a null selection means
  // "we don't know yet", not "the user picked nothing" — route guards and the
  // landing redirect must not act on the difference before it is settled.
  initialized: boolean
  fetchProperties: (allowedIds: number[]) => Promise<void>
  selectProperty: (id: number) => void
  reset: () => void
}

const STORAGE_KEY = 'qc.selected_property_id'

export const usePropertyStore = create<PropertyStore>((set, get) => ({
  properties: [],
  selectedPropertyId: (() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      return stored ? Number(stored) : null
    } catch {
      return null
    }
  })(),
  loading: false,
  initialized: false,

  fetchProperties: async (allowedIds: number[]) => {
    if (get().properties.length > 0) return
    set({ loading: true })
    try {
      const all = await propertiesService.getAll()
      const filtered = allowedIds.length > 0
        ? all.filter((p) => allowedIds.includes(p.id))
        : all
      // No auto-selection. A fresh session starts with nothing chosen so the
      // user picks a property deliberately — landing on whichever property
      // happened to sort first hid which one you were actually looking at.
      const currentSelected = get().selectedPropertyId
      const validSelection = currentSelected && filtered.some((p) => p.id === currentSelected)
      const selectedPropertyId = validSelection ? currentSelected : null

      if (selectedPropertyId) {
        sessionStorage.setItem(STORAGE_KEY, String(selectedPropertyId))
      } else {
        sessionStorage.removeItem(STORAGE_KEY)
      }

      set({ properties: filtered, selectedPropertyId, loading: false, initialized: true })
    } catch {
      // Settled either way — a failed fetch is still an answer, and leaving
      // initialized false would strand every guard on "we don't know yet".
      set({ loading: false, initialized: true })
    }
  },

  selectProperty: (id: number) => {
    sessionStorage.setItem(STORAGE_KEY, String(id))
    set({ selectedPropertyId: id })
  },

  reset: () => {
    sessionStorage.removeItem(STORAGE_KEY)
    set({ properties: [], selectedPropertyId: null, loading: false, initialized: false })
  },
}))

export function useSelectedPropertyName(): string | null {
  return usePropertyStore((state) => {
    if (state.selectedPropertyId === null) return null
    return state.properties.find((p) => p.id === state.selectedPropertyId)?.name ?? null
  })
}

export function useSelectedPropertyId(): number | null {
  return usePropertyStore((state) => state.selectedPropertyId)
}

export function useSelectedPropertyType(): PropertyType | null {
  return usePropertyStore((state) => {
    if (state.selectedPropertyId === null) return null
    return state.properties.find((p) => p.id === state.selectedPropertyId)?.property_type ?? null
  })
}
